import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { ROUTES } from "../lib/routes";
import { Figure } from "../components/Figure";
import {
  ARCHIVE_BYTES,
  DISCLOSURES,
  DISCLOSURE_COUNT,
} from "../lib/disclosures";
import { docUrl, formatBytes } from "../lib/docUrl";
import { DEANS, deanPhoto } from "../lib/deans";

/** Whoever currently holds the Public Relations deanship. */
const PR_DEAN = DEANS.find((d) => d.role.includes("Public Relations"));

// Read off the institute's own accreditation pages, not estimated. The NAAC
// line in particular used to claim "A+, CGPA 3.42, Cycle 3, valid to July 2029";
// bietdvg.edu/naac publishes Cycle 2 at 'A' grade, valid 08-07-2023 to 2028, and
// the certificate itself is linked in the NAAC section below.
const accreds = [
  { grade: "A", name: "NAAC", body: "Accredited in Cycle 2 with 'A' grade, valid five years from 08-07-2023. Cycle 1 carried the same grade from 2017." },
  { grade: "NBA", name: "Accredited programmes", body: "Programmes hold Letters of Approval running to 2028 — the current set covers EEE, Civil, CS, EC, ME, Chemical, Textiles, IS and Biotechnology." },
  { grade: "12(B)", name: "UGC", body: "Recognized under sections 2(f) and 12(B) of the UGC Act, 1956. Eligible for central grants." },
  { grade: "VTU", name: "Affiliation", body: "Affiliated to Visvesvaraya Technological University, Belagavi. AICTE approval is unbroken since 1992-93." },
];

const groups = [
  {
    title: "AICTE & UGC",
    docs: [
      { name: "AICTE Approval Letter — AY 2026-27", meta: "PDF · 8 PAGES · 2.4 MB", icon: "PDF" },
      { name: "AICTE EOA Order — All Programs", meta: "PDF · 12 PAGES · 3.1 MB", icon: "PDF" },
      { name: "UGC 2(f) and 12(B) Recognition", meta: "PDF · 4 PAGES · 1.2 MB", icon: "PDF" },
      { name: "VTU Affiliation Letter", meta: "PDF · 3 PAGES · 0.9 MB", icon: "PDF" },
    ],
  },
  {
    title: "NAAC",
    docs: [
      { name: "NAAC Cycle-3 Final Certificate (A+)", meta: "PDF · 2 PAGES · 0.8 MB", icon: "PDF" },
      { name: "Self-Study Report (SSR) — Cycle 3", meta: "PDF · 320 PAGES · 22 MB", icon: "PDF" },
      { name: "AQAR 2024-25", meta: "PDF · 78 PAGES · 5.4 MB", icon: "PDF" },
      { name: "AQAR 2023-24", meta: "PDF · 76 PAGES · 5.1 MB", icon: "PDF" },
    ],
  },
  {
    title: "NBA — Program-wise",
    docs: [
      { name: "NBA Certificate — Computer Science & Engineering", meta: "PDF · 2 PAGES", icon: "PDF" },
      { name: "NBA Certificate — Electronics & Communication", meta: "PDF · 2 PAGES", icon: "PDF" },
      { name: "NBA Certificate — Information Science & Engg.", meta: "PDF · 2 PAGES", icon: "PDF" },
      { name: "NBA Certificate — Mechanical Engineering", meta: "PDF · 2 PAGES", icon: "PDF" },
      { name: "NBA Certificate — Civil Engineering", meta: "PDF · 2 PAGES", icon: "PDF" },
      { name: "NBA Certificate — Electrical & Electronics", meta: "PDF · 2 PAGES", icon: "PDF" },
    ],
  },
  {
    title: "Governance & Finance",
    docs: [
      { name: "Governing Body — Composition & Minutes", meta: "PDF · 16 PAGES", icon: "PDF" },
      { name: "Audited Balance Sheet 2024-25", meta: "PDF · 42 PAGES · 3.8 MB", icon: "PDF" },
      { name: "Annual Report 2024-25", meta: "PDF · 96 PAGES · 14 MB", icon: "PDF" },
      { name: "Strategic Plan 2025-2030", meta: "PDF · 28 PAGES · 4.2 MB", icon: "PDF" },
    ],
  },
  {
    title: "Policies",
    docs: [
      { name: "Code of Conduct — Students", meta: "PDF · 18 PAGES", icon: "PDF" },
      { name: "Code of Conduct — Faculty & Staff", meta: "PDF · 14 PAGES", icon: "PDF" },
      { name: "Research & Publication Ethics Policy", meta: "PDF · 12 PAGES", icon: "PDF" },
      { name: "IPR Policy", meta: "PDF · 10 PAGES", icon: "PDF" },
      { name: "Energy & Environment Policy", meta: "PDF · 8 PAGES", icon: "PDF" },
      { name: "Equity & Inclusion Policy", meta: "PDF · 8 PAGES", icon: "PDF" },
    ],
  },
  {
    title: "Mandatory information",
    docs: [
      { name: "Mandatory Disclosure (AICTE format)", meta: "PDF · 24 PAGES · 2.1 MB", icon: "PDF" },
      { name: "Faculty list with qualifications", meta: "PDF · 12 PAGES", icon: "PDF" },
      { name: "Fee structure — All programs", meta: "PDF · 6 PAGES", icon: "PDF" },
      { name: "Sanctioned intake — Branch-wise", meta: "PDF · 4 PAGES", icon: "PDF" },
    ],
  },
];

const ALL = "all";

export default function Disclosures() {
  const { hash } = useLocation();
  const [active, setActive] = useState<string>(ALL);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  // Set once the user picks a tab, so arriving at #aicte scrolls to the list but
  // simply switching tabs afterwards does not yank the page around.
  const scrollOnNextRender = useRef(false);

  const tabs = [
    { key: ALL, label: "All", count: DISCLOSURE_COUNT },
    ...DISCLOSURES.map((g) => ({ key: g.slug, label: g.title, count: g.docs.length })),
  ];

  // The hero's jump links are plain `#slug` anchors, so a section hash — typed,
  // bookmarked, or clicked — selects that tab. One mechanism, two entry points,
  // and every section stays deep-linkable even though only one renders at a time.
  useEffect(() => {
    const slug = decodeURIComponent(hash.replace(/^#/, ""));
    if (!slug) return;
    if (DISCLOSURES.some((g) => g.slug === slug)) {
      setActive(slug);
      scrollOnNextRender.current = true;
    }
  }, [hash]);

  useEffect(() => {
    if (!scrollOnNextRender.current) return;
    scrollOnNextRender.current = false;
    listRef.current?.scrollIntoView({ block: "start" });
  }, [active]);

  const shown = active === ALL ? DISCLOSURES : DISCLOSURES.filter((g) => g.slug === active);

  // Roving-focus keyboard nav across the tab strip (WAI-ARIA tabs pattern),
  // matching the Alumni and Committees strips.
  function onTabKeyDown(e: React.KeyboardEvent, i: number) {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!delta && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : (i + delta + tabs.length) % tabs.length;
    setActive(tabs[next].key);
    tabRefs.current[next]?.focus();
  }

  return (
    <>
      <PageHero
        variant="hero-navy"
        eyebrow="Statutory disclosures"
        title="Every approval, certificate and audited account."
        lead="AICTE approvals back to the institute's first in 1992-93, NBA and NAAC certificates, annual quality reports, NIRF returns and audited accounts — each the institute's own published file, downloadable here."
        crumbs={[{ label: "About", to: ROUTES.about }, { label: "Disclosures" }]}
        /* No banner: an archive has nothing to illustrate, and a stock image
           beside statutory records reads as decoration. The column carries the
           shape of the archive instead — every figure below is counted from
           DISCLOSURES, so it cannot drift from what the page actually lists. */
        aside={
          <div className="hero-facts">
            <div className="hf-row">
              <span className="hf-num">{DISCLOSURE_COUNT}</span>
              <span className="hf-lbl">Documents</span>
            </div>
            <div className="hf-row">
              <span className="hf-num">{DISCLOSURES.length}</span>
              <span className="hf-lbl">Sections</span>
            </div>
            <div className="hf-row">
              <span className="hf-num">{formatBytes(ARCHIVE_BYTES)}</span>
              <span className="hf-lbl">Total size</span>
            </div>
            <ul className="hf-jump">
              {DISCLOSURES.map((g) => (
                <li key={g.slug}>
                  <a href={`#${g.slug}`}>
                    {g.title} <span>{g.docs.length}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        }
      />
      <section className="section">
        <div className="container">
          <div className="kicker">Headline accreditations</div>
          <div className="accred-strip-big">
            {accreds.map((a, i) => (
              <div key={i} className="a reveal">
                <div className="grade">{a.grade}</div>
                <p className="body">{a.body}</p>
                <div className="name">{a.name}</div>
              </div>
            ))}
          </div>

          <div className="disc-filter" ref={listRef}>
            <div className="kicker">Browse the archive</div>
            <div className="filter-tabs" role="tablist" aria-label="Filter disclosures by section">
              {tabs.map((t, i) => (
                <button
                  key={t.key}
                  ref={(el) => { tabRefs.current[i] = el; }}
                  role="tab"
                  type="button"
                  id={`disc-tab-${i}`}
                  aria-selected={active === t.key}
                  aria-controls="disc-panel"
                  tabIndex={active === t.key ? 0 : -1}
                  className={"filter-tab" + (active === t.key ? " is-active" : "")}
                  onClick={() => setActive(t.key)}
                  onKeyDown={(e) => onTabKeyDown(e, i)}
                >
                  {t.label}
                  <span className="filter-tab-count">{t.count}</span>
                </button>
              ))}
            </div>
          </div>

          <div
            id="disc-panel"
            role="tabpanel"
            aria-labelledby={`disc-tab-${tabs.findIndex((t) => t.key === active)}`}
          >
          {shown.map((g) => (
            <div key={g.slug} id={g.slug} style={{ marginTop: 80 }}>
              <h2 style={{ fontSize: "clamp(26px, 2.6vw, 36px)", fontWeight: 600 }}>{g.title}</h2>
              <p className="disc-blurb">{g.blurb}</p>
              <div className="disc-list">
                {g.docs.map((d) => (
                  <a
                    key={d.url}
                    className="disc-item reveal"
                    href={docUrl(d.url)}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <div className="disc-icon">PDF</div>
                    <div className="disc-info">
                      <div className="name">{d.name}</div>
                      <div className="meta">PDF · {formatBytes(d.bytes)}</div>
                    </div>
                    <span className="disc-action">Download →</span>
                  </a>
                ))}
              </div>
            </div>
          ))}
          </div>

          {/* Named contact for anything this archive does not answer. The dean and
              portrait come from the deans roster; the phone and address are the
              institute's own published lines, not a desk number invented for this
              page. */}
          {PR_DEAN && (
            <div className="report-card pr-card" style={{ marginTop: 80 }}>
              <div className="pr-photo">
                <Figure
                  media={deanPhoto(PR_DEAN)}
                  fallbackLabel={PR_DEAN.name}
                  aspect="3/4"
                  className="pr-photo-fig"
                  sizes="(max-width: 700px) 40vw, 160px"
                />
              </div>
              <div className="pr-body">
                <h3>Public relations</h3>
                <p>
                  For anything this archive does not answer — document requests, press
                  and media enquiries, or verification of an approval or accreditation —
                  the Dean of Public Relations is the institute's point of contact.
                </p>
                <div className="pr-person">
                  <span className="pr-name">{PR_DEAN.name}</span>
                  <span className="pr-role">{PR_DEAN.role}</span>
                </div>
              </div>
              <div className="actions">
                <span className="hot">INSTITUTE OFFICE</span>
                <span className="num">+91 8192 233 800</span>
                <a
                  className="btn-link"
                  href="mailto:info@bietdvg.edu"
                  style={{ color: "var(--gold-400)", borderBottomColor: "var(--gold-400)" }}
                >
                  info@bietdvg.edu →
                </a>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
